import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { ArrowLeft, Plus, FileText, Eye, X } from "lucide-react";
import { QuoteRequest, RateAnalysis } from "../../types/rate-analysis";
import RefiBuddyResults from "../RefiBuddyResults";
import QuoteAnalysisPage from "../QuoteAnalysisPage";
import Toast from "../Toast";
import AnalysisSuggestions from "./AnalysisSuggestions";
import { adminApi } from "../../utils/adminApi";
import { isRefiBuddyRequest } from "../../utils/quoteRequestUtils";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const stateName = import.meta.env.VITE_STATE_NAME || "";

interface QuoteOption {
  rate: number;
  lenderFees: number;
  mortgageInsurance: number;
  apr: number;
  isRecommended: boolean;
}

interface AnalysisFormData {
  quoteRequestId: string;
  analysis: {
    recommendations?: string[];
    notes?: string[];
    pros: string[];
    cons: string[];
    grade: string;
    lender: "provident" | "rocket";
  };
  quotes: QuoteOption[];
}

const QuoteAnalysis: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [quotes, setQuotes] = useState<QuoteOption[]>([
    {
      rate: 0,
      lenderFees: 0,
      mortgageInsurance: 0,
      apr: 0,
      isRecommended: false,
    },
  ]);
  const [pros, setPros] = useState<string[]>([""]);
  const [cons, setCons] = useState<string[]>([""]);
  const [notes, setNotes] = useState<string[]>([""]);
  const [recommendation, setRecommendation] = useState<string>("");
  const [grade, setGrade] = useState<string>("");
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState<{
    analysis: RateAnalysis;
    quoteRequest: QuoteRequest;
  } | null>(null);
  const [brokerRate, setBrokerRate] = useState<number | undefined>();
  const [brokerLenderFees, setBrokerLenderFees] = useState<
    number | undefined
  >();
  const [brokerMortgageInsurance, setBrokerMortgageInsurance] = useState<
    number | undefined
  >();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState<{
    pros?: string;
    cons?: string;
    grade?: string;
    lender?: string;
  }>({});
  const [lender, setLender] = useState<"provident" | "rocket" | "">("");

  // Add after existing state declarations
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailButtonLink, setEmailButtonLink] = useState("");
  const [emailButtonText, setEmailButtonText] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  // Fetch quote request details
  const {
    data: quoteRequest,
    isLoading: isLoadingRequest,
  }: { data: QuoteRequest | undefined; isLoading: boolean } =
    useQuery<QuoteRequest>(["quoteRequest", requestId], async () => {
      const response = await fetch(
        `${API_BASE_URL}/quote-requests/${requestId}`
      );
      if (!response.ok) throw new Error("Failed to fetch quote request");
      return response.json();
    });

  // Fetch existing analysis
  const {
    data: analysis,
    isLoading: isLoadingAnalysis,
  }: { data: RateAnalysis | undefined; isLoading: boolean } =
    useQuery<RateAnalysis>(["analysis", requestId], async () => {
      const response = await fetch(
        `${API_BASE_URL}/analysis/request/${requestId}`
      );
      if (!response.ok) throw new Error("Failed to fetch analysis");
      return response.json();
    });

  // Initialize form data when analysis is loaded
  useEffect(() => {
    if (analysis) {
      // Set pros and cons
      setPros(analysis.pros.length > 0 ? analysis.pros : [""]);
      setCons(analysis.cons.length > 0 ? analysis.cons : [""]);

      // Handle notes vs recommendations based on analysis type
      if (analysis.notes && analysis.notes.length > 0) {
        setNotes(analysis.notes);
      } else if (
        analysis.recommendations &&
        analysis.recommendations.length > 0
      ) {
        setRecommendation(analysis.recommendations[0]);
      }

      setGrade(analysis.grade || "");
      setLender(analysis.lender || "");

      // Set quotes from quote request
      if (
        quoteRequest &&
        quoteRequest.quotes &&
        quoteRequest.quotes.length > 0
      ) {
        setQuotes(
          quoteRequest.quotes.map((quote) => ({
            rate: quote.rate,
            lenderFees: quote.lenderFees,
            mortgageInsurance: quote.mortgageInsurance,
            apr: typeof quote.apr === "number" ? quote.apr : 0,
            isRecommended: quote.isRecommended || false,
          }))
        );
      }
    }
  }, [analysis, quoteRequest]);

  // Initialize broker data when quote request is loaded
  useEffect(() => {
    if (quoteRequest) {
      setBrokerRate(quoteRequest.broker_rate || undefined);
      setBrokerLenderFees(quoteRequest.broker_lender_fees || undefined);
      setBrokerMortgageInsurance(
        quoteRequest.broker_mortgage_insurance || undefined
      );
    }
  }, [quoteRequest]);

  // Submit analysis and quotes mutation
  const submitAnalysisMutation = useMutation(
    async (formData: AnalysisFormData) => {
      return adminApi.post(`/quote-requests/${requestId}/analysis`, {
        analysis: formData.analysis,
        quotes: formData.quotes,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["analysis", requestId]);
        queryClient.invalidateQueries(["quoteRequest", requestId]);
        queryClient.invalidateQueries("pendingQuoteRequests");
        queryClient.invalidateQueries("analyzedQuoteRequests");
        setIsPreviewMode(false);
        setToastMessage("Analysis submitted successfully");
        setShowToast(true);
      },
    }
  );

  // Add mutation for saving broker quote
  // const saveBrokerQuoteMutation = useMutation(
  //   async () => {
  //     const response = await fetch(
  //       `${API_BASE_URL}/quote-requests/${requestId}/broker-quote`,
  //       {
  //         method: "PATCH",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify({
  //           broker_rate: brokerRate,
  //           broker_lender_fees: brokerLenderFees,
  //           broker_mortgage_insurance: brokerMortgageInsurance,
  //         }),
  //       }
  //     );
  //     if (!response.ok) throw new Error("Failed to save broker quote");
  //     return response.json();
  //   },
  //   {
  //     onSuccess: () => {
  //       queryClient.invalidateQueries(["quoteRequest", requestId]);
  //       setToastMessage("Broker quote saved successfully");
  //       setShowToast(true);
  //     },
  //   }
  // );

  const addQuote = () => {
    setQuotes([
      ...quotes,
      {
        rate: 0,
        lenderFees: 0,
        mortgageInsurance: 0,
        apr: 0,
        isRecommended: false,
      },
    ]);
  };

  const removeQuote = (index: number) => {
    setQuotes(quotes.filter((_, i) => i !== index));
  };

  const updateQuote = (
    index: number,
    field: keyof QuoteOption,
    value: string | boolean
  ) => {
    const newQuotes = [...quotes];
    if (field === "isRecommended") {
      newQuotes[index] = { ...newQuotes[index], [field]: value as boolean };
    } else {
      // Allow empty string for negative numbers
      const numValue = value === "" ? 0 : parseFloat(value as string);
      newQuotes[index] = { ...newQuotes[index], [field]: numValue };
    }
    setQuotes(newQuotes);
  };

  const addPro = () => {
    setPros([...pros, ""]);
  };

  const removePro = (index: number) => {
    setPros(pros.filter((_, i) => i !== index));
  };

  const updatePro = (index: number, value: string) => {
    const newPros = [...pros];
    newPros[index] = value;
    setPros(newPros);
  };

  const addCon = () => {
    setCons([...cons, ""]);
  };

  const removeCon = (index: number) => {
    setCons(cons.filter((_, i) => i !== index));
  };

  const updateCon = (index: number, value: string) => {
    const newCons = [...cons];
    newCons[index] = value;
    setCons(newCons);
  };

  const addNote = () => {
    setNotes([...notes, ""]);
  };

  const removeNote = (index: number) => {
    setNotes(notes.filter((_, i) => i !== index));
  };

  const updateNote = (index: number, value: string) => {
    const newNotes = [...notes];
    newNotes[index] = value;
    setNotes(newNotes);
  };

  const handleSelectSuggestion = (
    type: "recommendations" | "pros" | "cons" | "notes",
    value: string
  ) => {
    switch (type) {
      case "recommendations":
        setRecommendation(value);
        break;
      case "pros": {
        // Add to the first empty pro field or create a new one
        const emptyProIndex = pros.findIndex((pro) => !pro.trim());
        if (emptyProIndex !== -1) {
          updatePro(emptyProIndex, value);
        } else {
          setPros([...pros, value]);
        }
        break;
      }
      case "cons": {
        // Add to the first empty con field or create a new one
        const emptyConIndex = cons.findIndex((con) => !con.trim());
        if (emptyConIndex !== -1) {
          updateCon(emptyConIndex, value);
        } else {
          setCons([...cons, value]);
        }
        break;
      }
      case "notes": {
        // Add to the first empty note field or create a new one
        const emptyNoteIndex = notes.findIndex((note) => !note.trim());
        if (emptyNoteIndex !== -1) {
          updateNote(emptyNoteIndex, value);
        } else {
          setNotes([...notes, value]);
        }
        break;
      }
    }
  };

  const validateAnalysisFields = (formData: FormData) => {
    const errors: {
      pros?: string;
      cons?: string;
      grade?: string;
      recommendation?: string;
      lender?: string;
    } = {};
    const hasBrokerRate = !!brokerRate;
    const filteredPros = pros.filter(Boolean);
    const filteredCons = cons.filter(Boolean);
    const grade = formData.get("grade") as string;

    // Validate lender is selected
    if (!lender) {
      errors.lender = "Please select a lender";
    }

    // Only validate these fields if there's a broker rate
    if (hasBrokerRate) {
      const hasAnyField =
        filteredPros.length > 0 || filteredCons.length > 0 || grade;
      const hasAllFields =
        filteredPros.length > 0 && filteredCons.length > 0 && grade;

      if (hasAnyField && !hasAllFields) {
        if (filteredPros.length === 0)
          errors.pros = "Pros are required when analyzing a broker quote";
        if (filteredCons.length === 0)
          errors.cons = "Cons are required when analyzing a broker quote";
        if (!grade)
          errors.grade = "Grade is required when analyzing a broker quote";
      }
    }

    // Validate recommendation if it's shown (not a refinance with current mortgage)
    if (!isRefinanceWithCurrentMortgage && !recommendation.trim()) {
      errors.recommendation = "Overall recommendation is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitAnalysis = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    if (!requestId) return;

    // Validate the form
    if (!validateAnalysisFields(formData)) {
      return;
    }

    // First update the quote request with broker rate and fees if they exist
    if (brokerRate || brokerLenderFees || brokerMortgageInsurance) {
      try {
        await fetch(
          `${API_BASE_URL}/quote-requests/${requestId}/broker-quote`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              broker_rate: brokerRate,
              broker_lender_fees: brokerLenderFees,
              broker_mortgage_insurance: brokerMortgageInsurance,
            }),
          }
        );
      } catch (error) {
        console.error("Error updating quote request:", error);
      }
    }

    // Then submit the analysis
    const analysisData: AnalysisFormData = {
      quoteRequestId: requestId,
      analysis: {
        ...(isRefinanceWithCurrentMortgage
          ? { notes: notes.filter(Boolean) }
          : { recommendations: [recommendation] }),
        pros: pros.filter(Boolean),
        cons: cons.filter(Boolean),
        grade: grade as "A" | "B" | "C" | "D" | "F",
        lender: lender as "provident" | "rocket",
      },
      quotes,
    };

    submitAnalysisMutation.mutate(analysisData);
  };

  const handlePreview = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const form = e.currentTarget.closest("form");
    if (!form || !quoteRequest) return;

    const formData = new FormData(form);

    // Validate before preview
    if (!validateAnalysisFields(formData)) {
      return;
    }

    const previewAnalysis: RateAnalysis = {
      id: "preview",
      quote_request_id: requestId || "",
      ...(isRefinanceWithCurrentMortgage
        ? { notes: notes.filter(Boolean) }
        : { recommendations: [recommendation] }),
      pros: pros.filter(Boolean),
      cons: cons.filter(Boolean),
      grade: grade as "A" | "B" | "C" | "D" | "F",
      lender: lender as "provident" | "rocket",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Create a preview quote request that matches what the user will see
    const previewQuoteRequest: QuoteRequest = {
      ...quoteRequest,
      // For purchase loans, use purchase_price instead of property_value
      property_value:
        quoteRequest.loan_type === "purchase"
          ? quoteRequest.purchase_price || 0
          : quoteRequest.property_value || 0,
      broker_rate: brokerRate,
      broker_lender_fees: brokerLenderFees,
      broker_mortgage_insurance: brokerMortgageInsurance,
      quotes: [
        // Add our new quotes
        ...quotes.map((quote) => ({
          id: "preview",
          rate: quote.rate,
          lenderFees: quote.lenderFees,
          mortgageInsurance: quote.mortgageInsurance,
          apr: quote.apr,
          isRecommended: quote.isRecommended,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          points: 0,
          titleFees: 0,
          source: "SYSTEM" as const,
        })),
      ],
    };

    setPreviewData({
      analysis: previewAnalysis,
      quoteRequest: previewQuoteRequest,
    });
    setIsPreviewMode(true);
  };

  const hasQuoteToAnalyze =
    !!quoteRequest?.broker_rate || !!quoteRequest?.user_uploaded_quote_url;

  const isRefinanceWithCurrentMortgage =
    quoteRequest?.loan_type === "refinance" && !!quoteRequest?.current_mortgage;

  useEffect(() => {
    setEmailTo(quoteRequest?.email || "");
    setEmailSubject(
      hasQuoteToAnalyze
        ? "Your Quote Analysis is Ready!"
        : "Your Quote Estimates are Ready!"
    );
    setEmailButtonLink(
      `${window.location.origin}/rate-results/?qr=${requestId}`
    );
    setEmailButtonText(hasQuoteToAnalyze ? "View Analysis" : "View Quotes");
    setEmailMessage(
      hasQuoteToAnalyze
        ? "Great news — your mortgage quote has been reviewed by a licensed broker, and your personalized analysis is ready."
        : "Great news — your mortgage loan scenario has been reviewed by a licensed broker, and your personalized quote estimates are ready."
    );
  }, [quoteRequest, hasQuoteToAnalyze, requestId]);

  const sendEmailMutation = useMutation(
    async () => {
      setEmailError(null);
      const res = await adminApi.post("/send-email", {
        to: emailTo,
        subject: emailSubject,
        button_link: emailButtonLink,
        button_text: emailButtonText,
        message: emailMessage,
        state: quoteRequest?.state || "",
      });
      return res;
    },
    {
      onSuccess: () => {
        setToastMessage("Email sent successfully");
        setShowToast(true);
      },
      onError: (err: unknown) => {
        setEmailError((err as Error)?.message || "Failed to send email");
        setToastMessage("Failed to send email");
        setShowToast(true);
      },
    }
  );

  // Fetch analysis suggestions
  const { data: suggestions, isLoading: isLoadingSuggestions } = useQuery(
    ["analysisSuggestions"],
    async () => {
      return adminApi.get("/analysis-suggestions");
    }
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoadingRequest || isLoadingAnalysis) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!quoteRequest) {
    return (
      <div className="text-center py-8 text-gray-500">
        Quote request not found
      </div>
    );
  }

  if (isPreviewMode && previewData) {
    // Use the same logic as RateResults.tsx to determine which component to show
    const isRefiBuddy = isRefiBuddyRequest(previewData.quoteRequest);

    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <button
            onClick={() => setIsPreviewMode(false)}
            className="inline-flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Edit
          </button>
          <h2 className="text-xl font-semibold">Preview Analysis</h2>
        </div>
        <div className="bg-white rounded-lg shadow-sm">
          {previewData &&
            (isRefiBuddy ? (
              <RefiBuddyResults
                quoteRequest={previewData.quoteRequest}
                analysis={previewData.analysis}
                disableTracking={true}
              />
            ) : (
              <QuoteAnalysisPage
                quoteRequest={previewData.quoteRequest}
                analysis={previewData.analysis}
                disableTracking={true}
              />
            ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
      {/* Back Button */}
      <button
        onClick={() => navigate("/admin")}
        className="inline-flex items-center text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft size={20} className="mr-2" />
        Back to Dashboard
      </button>
      {/* Quote Request Details and Contact Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Quote Request Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Quote Request Details</h2>
          <div className="grid grid-cols-2 gap-4">
            {quoteRequest.loan_type === "purchase" ? (
              <>
                <div>
                  <p className="text-sm text-gray-600">Purchase Price</p>
                  <p className="font-medium">
                    ${(quoteRequest.purchase_price || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Down Payment</p>
                  <p className="font-medium">
                    ${(quoteRequest.down_payment || 0).toLocaleString()}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-sm text-gray-600">Property Value</p>
                  <p className="font-medium">
                    ${(quoteRequest.property_value || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Remaining Balance</p>
                  <p className="font-medium">
                    ${(quoteRequest.remaining_balance || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Cash Out</p>
                  <p className="font-medium">
                    {quoteRequest.cash_out ? "Yes" : "No"}
                  </p>
                </div>
                {quoteRequest.cash_out && (
                  <div>
                    <p className="text-sm text-gray-600">Cash Out Amount</p>
                    <p className="font-medium">
                      ${(quoteRequest.cash_out_amount || 0).toLocaleString()}
                    </p>
                  </div>
                )}
              </>
            )}
            <div>
              <p className="text-sm text-gray-600">Credit Score</p>
              <p className="font-medium">{quoteRequest.credit_score}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Zip Code</p>
              <p className="font-medium">{quoteRequest.zip_code}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Property Type</p>
              <p className="font-medium">{quoteRequest.property_type}</p>
            </div>
            {quoteRequest.loan_type === "purchase" && (
              <div>
                <p className="text-sm text-gray-600">First Home</p>
                <p className="font-medium">
                  {quoteRequest.is_first_home ? "Yes" : "No"}
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600">Loan Type</p>
              <p className="font-medium">{quoteRequest.loan_type}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Property Style</p>
              <p className="font-medium">{quoteRequest.property_style}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Created At</p>
              <p className="font-medium">
                {formatDate(quoteRequest.created_at)}
              </p>
            </div>
          </div>

          {/* Request ID and Results Page */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Quote Request ID</p>
                <p className="font-mono text-sm font-medium">{requestId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Results Page</p>
                <a
                  href={`/rate-results/?qr=${requestId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark font-medium text-sm"
                >
                  View Results Page →
                </a>
                <p className="font-mono text-xs text-gray-500 mt-1">
                  {window.location.origin}/rate-results/?qr={requestId}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium">{quoteRequest.email || "None"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Phone</p>
              <p className="font-medium">{quoteRequest.phone || "None"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Preferred Contact Method</p>
              <p className="font-medium">
                {quoteRequest.preferred_contact_method
                  ? quoteRequest.preferred_contact_method
                      .charAt(0)
                      .toUpperCase() +
                    quoteRequest.preferred_contact_method.slice(1)
                  : "None"}
              </p>
            </div>
          </div>

          {/* Email Copy - Only show if analysis exists */}
          {analysis && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Email Copy
              </h3>

              {/* Subject */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-gray-700">Subject</p>
                  <button
                    onClick={() => {
                      const subject = hasQuoteToAnalyze
                        ? "Your Quote Analysis is Ready!"
                        : "Your Quote Estimates are Ready!";
                      navigator.clipboard.writeText(subject);
                    }}
                    className="text-sm text-primary hover:text-primary-dark font-medium"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                  {hasQuoteToAnalyze
                    ? "Your Quote Analysis is Ready!"
                    : "Your Quote Estimates are Ready!"}
                </p>
              </div>

              {/* Body */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-gray-700">Body</p>
                  <button
                    onClick={() => {
                      const body = hasQuoteToAnalyze
                        ? `Hi,\n\nGreat news — your mortgage quote has been reviewed by a licensed broker, and your personalized analysis is ready.\n\n\n${stateName} Mortgage Tools to see your quote analysis.\n${window.location.origin}/rate-results/?qr=${requestId}\n\nIf you have any questions or want to talk through your options, we're here to help.\n\n— ${stateName} Mortgage Tools team`
                        : `Hi,\n\nGreat news — your mortgage loan scenario has been reviewed by a licensed broker, and your personalized quote estimates are ready.\n\n\n${stateName} Mortgage Tools to see your quote estimates.\n${window.location.origin}/rate-results/?qr=${requestId}\n\nIf you have any questions or want to talk through your options, we're here to help.\n\n— ${stateName} Mortgage Tools team`;
                      navigator.clipboard.writeText(body);
                    }}
                    className="text-sm text-primary hover:text-primary-dark font-medium"
                  >
                    Copy
                  </button>
                </div>
                <div className="bg-gray-50 p-3 rounded border">
                  <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans">
                    {hasQuoteToAnalyze
                      ? `Hi,

Great news — your mortgage quote has been reviewed by a licensed broker, and your personalized analysis is ready.


${stateName} Mortgage Tools to see your quote analysis.
${window.location.origin}/rate-results/?qr=${requestId}

If you have any questions or want to talk through your options, we're here to help.

— ${stateName} Mortgage Tools team`
                      : `Hi,

Great news — your mortgage loan scenario has been reviewed by a licensed broker, and your personalized quote estimates are ready.


${stateName} Mortgage Tools to see your quote estimates.
${window.location.origin}/rate-results/?qr=${requestId}

If you have any questions or want to talk through your options, we're here to help.

— ${stateName} Mortgage Tools team`}
                  </pre>
                </div>
              </div>
            </div>
          )}
          {/* New: Editable Email Send Section */}
          <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Send Email (Mailgun)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To
                </label>
                <input
                  type="email"
                  className="input-field w-full"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="Recipient email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Subject"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Button Link
                </label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={emailButtonLink}
                  onChange={(e) => setEmailButtonLink(e.target.value)}
                  placeholder="Button link URL"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Button Text
                </label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={emailButtonText}
                  onChange={(e) => setEmailButtonText(e.target.value)}
                  placeholder="Button text"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                className="input-field w-full"
                rows={3}
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Email message"
              />
            </div>
            {emailError && (
              <div className="text-red-600 text-sm mb-2">{emailError}</div>
            )}
            <button
              type="button"
              className="btn-primary"
              onClick={() => sendEmailMutation.mutate()}
              disabled={sendEmailMutation.isLoading}
            >
              {sendEmailMutation.isLoading ? "Sending..." : "Send Email"}
            </button>
          </div>
        </div>
      </div>

      {/* Combined Analysis and Quotes Form */}
      <form
        id="analysisForm"
        onSubmit={handleSubmitAnalysis}
        className="bg-white rounded-lg shadow-sm"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-heading font-semibold text-gray-900">
            {analysis ? "Edit Analysis" : "Create Analysis"}
          </h2>
          {!hasQuoteToAnalyze && (
            <p className="mt-2 text-sm text-gray-600">
              Add a broker rate or upload a quote to enable analysis fields
            </p>
          )}
        </div>
        <div className="p-6 space-y-6">
          {/* Quote Options Section - Moved to top */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Quote Options
              </h3>
              <div className="flex items-center space-x-4">
                {/* Lender Selection */}
                <div className="flex items-center space-x-2">
                  <label
                    htmlFor="lender"
                    className="text-sm font-medium text-gray-700"
                  >
                    Lender:
                  </label>
                  <select
                    id="lender"
                    name="lender"
                    className={`input-field ${
                      validationErrors.lender ? "border-red-500" : ""
                    }`}
                    value={lender}
                    onChange={(e) => {
                      setLender(e.target.value as "provident" | "rocket");
                      if (validationErrors.lender) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          lender: undefined,
                        }));
                      }
                    }}
                    required
                  >
                    <option value="">Choose lender</option>
                    <option value="provident">Provident</option>
                    <option value="rocket">Rocket</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={addQuote}
                  className="btn-primary inline-flex items-center"
                >
                  <Plus size={20} className="mr-2" />
                  Add Quote
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {quotes.map((quote, index) => (
                <div
                  key={index}
                  className="p-4 border border-gray-200 rounded-lg space-y-4"
                >
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">Quote {index + 1}</h4>
                    {quotes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeQuote(index)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <X size={20} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rate (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={quote.rate || ""}
                        onChange={(e) =>
                          updateQuote(index, "rate", e.target.value)
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full input-field"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lender Fees ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={quote.lenderFees || ""}
                        onChange={(e) =>
                          updateQuote(index, "lenderFees", e.target.value)
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full input-field"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mortgage Insurance ($)
                      </label>
                      <input
                        type="number"
                        value={quote.mortgageInsurance || ""}
                        onChange={(e) =>
                          updateQuote(
                            index,
                            "mortgageInsurance",
                            e.target.value
                          )
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        APR (%)
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        value={quote.apr || ""}
                        onChange={(e) =>
                          updateQuote(index, "apr", e.target.value)
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full input-field"
                        required
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={quote.isRecommended}
                          onChange={(e) =>
                            updateQuote(
                              index,
                              "isRecommended",
                              e.target.checked
                            )
                          }
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-700">
                          Recommended
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* User's Quote Section */}
          {hasQuoteToAnalyze && (
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h2 className="text-xl font-semibold mb-4">User's Quote</h2>
              <div className="space-y-4">
                {quoteRequest.user_uploaded_quote_url && (
                  <div className="flex items-center gap-4">
                    <a
                      href={`https://${import.meta.env.VITE_R2_PUBLIC_URL}/${
                        quoteRequest.user_uploaded_quote_url
                      }`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
                    >
                      <FileText className="w-4 h-4" />
                      View Attachment
                    </a>
                    <p className="text-sm text-gray-600">
                      {quoteRequest.user_uploaded_quote_url}
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label
                        htmlFor="broker_rate"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Rate (%)
                      </label>
                      <input
                        type="number"
                        id="broker_rate"
                        name="broker_rate"
                        step="0.125"
                        className="input-field w-full"
                        placeholder="Enter rate"
                        value={brokerRate || ""}
                        onChange={(e) =>
                          setBrokerRate(parseFloat(e.target.value))
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="broker_lender_fees"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Lender Fees ($)
                      </label>
                      <input
                        type="number"
                        id="broker_lender_fees"
                        name="broker_lender_fees"
                        className="input-field w-full"
                        placeholder="Enter lender fees"
                        value={brokerLenderFees || ""}
                        onChange={(e) =>
                          setBrokerLenderFees(parseFloat(e.target.value))
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="broker_mortgage_insurance"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Monthly MI ($)
                      </label>
                      <input
                        type="number"
                        id="broker_mortgage_insurance"
                        name="broker_mortgage_insurance"
                        className="input-field w-full"
                        placeholder="Enter monthly MI"
                        value={brokerMortgageInsurance || ""}
                        onChange={(e) =>
                          setBrokerMortgageInsurance(parseFloat(e.target.value))
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Overall Recommendation or Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRefinanceWithCurrentMortgage
                ? "Notes"
                : "Overall Recommendation"}
            </label>
            {isRefinanceWithCurrentMortgage ? (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">
                    Add notes about the refinance analysis
                  </span>
                  <button
                    type="button"
                    onClick={addNote}
                    className="text-sm text-primary hover:text-primary-dark"
                  >
                    + Add Note
                  </button>
                </div>
                <div className="space-y-2">
                  {notes.map((note, index) => (
                    <div key={index} className="flex gap-2">
                      <textarea
                        rows={2}
                        value={note}
                        onChange={(e) => updateNote(index, e.target.value)}
                        className="flex-1 input-field"
                        placeholder={`Note ${index + 1}`}
                      />
                      {notes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeNote(index)}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <X size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <textarea
                name="recommendations"
                rows={3}
                className="w-full input-field"
                placeholder="Enter your overall recommendation..."
                required
                value={recommendation}
                onChange={(e) => setRecommendation(e.target.value)}
              />
            )}
          </div>

          {/* Show analysis fields if there's a quote to analyze */}
          {hasQuoteToAnalyze && (
            <>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Pros
                  </label>
                  <button
                    type="button"
                    onClick={addPro}
                    className="text-sm text-primary hover:text-primary-dark"
                  >
                    + Add Pro
                  </button>
                </div>
                <div className="space-y-2">
                  {pros.map((pro, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={pro}
                        onChange={(e) => {
                          updatePro(index, e.target.value);
                          if (validationErrors.pros) {
                            setValidationErrors((prev) => ({
                              ...prev,
                              pros: undefined,
                            }));
                          }
                        }}
                        className={`flex-1 input-field ${
                          validationErrors.pros ? "border-red-500" : ""
                        }`}
                        placeholder={`Pro ${index + 1}`}
                      />
                      {pros.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePro(index)}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <X size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                  {validationErrors.pros && (
                    <p className="text-sm text-red-600 mt-1">
                      {validationErrors.pros}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Cons
                  </label>
                  <button
                    type="button"
                    onClick={addCon}
                    className="text-sm text-primary hover:text-primary-dark"
                  >
                    + Add Con
                  </button>
                </div>
                <div className="space-y-2">
                  {cons.map((con, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={con}
                        onChange={(e) => {
                          updateCon(index, e.target.value);
                          if (validationErrors.cons) {
                            setValidationErrors((prev) => ({
                              ...prev,
                              cons: undefined,
                            }));
                          }
                        }}
                        className={`flex-1 input-field ${
                          validationErrors.cons ? "border-red-500" : ""
                        }`}
                        placeholder={`Con ${index + 1}`}
                      />
                      {cons.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCon(index)}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <X size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                  {validationErrors.cons && (
                    <p className="text-sm text-red-600 mt-1">
                      {validationErrors.cons}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grade
                </label>
                <select
                  name="grade"
                  className={`w-full input-field ${
                    validationErrors.grade ? "border-red-500" : ""
                  }`}
                  value={grade}
                  onChange={(e) => {
                    setGrade(e.target.value);
                    if (validationErrors.grade) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        grade: undefined,
                      }));
                    }
                  }}
                >
                  <option value="">Select a grade</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                  <option value="F">F</option>
                </select>
                {validationErrors.grade && (
                  <p className="text-sm text-red-600 mt-1">
                    {validationErrors.grade}
                  </p>
                )}
              </div>
            </>
          )}

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handlePreview}
              className="btn-secondary inline-flex items-center"
            >
              <Eye size={20} className="mr-2" />
              Preview
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitAnalysisMutation.isLoading}
            >
              {submitAnalysisMutation.isLoading
                ? "Submitting..."
                : "Submit Analysis"}
            </button>
          </div>

          {/* Analysis Suggestions */}
          <AnalysisSuggestions
            suggestions={
              suggestions || { recommendations: [], pros: [], cons: [] }
            }
            onSelectSuggestion={handleSelectSuggestion}
            isLoading={isLoadingSuggestions}
            showProsCons={hasQuoteToAnalyze}
            showNotes={isRefinanceWithCurrentMortgage}
            showRecommendations={!isRefinanceWithCurrentMortgage}
          />
        </div>
      </form>
    </div>
  );
};

export default QuoteAnalysis;
